/** Seed data for the Supplier Profile screen (Demo Mode only, local-only - no backend
 * persistence yet). Extends the existing supplier governance profile
 * (see `supplierGovernanceData.ts`) with the additional record types needed for the
 * Overview/Legal & Commercial/Contacts/Coverage & Sites/Catalogues/Documents &
 * Certifications/Contracts & Renewals/Exceptions/Activity tabs. Performance tab reuses
 * `SupplierGovernanceProfile.performance`. Keyed by supplierId, falling back to a generated
 * default for any supplier not explicitly seeded below - same precedent as
 * `supplierGovernanceData.ts`. */

export enum SupplierOperationalStatus {
  Active = 'Active',
  OnHold = 'OnHold',
  Suspended = 'Suspended',
}

export interface SupplierDirectoryEntry {
  supplierId: number;
  supplierName: string;
  supplierCategory: string;
  supplierType: string;
  coverageType: string;
  operationalStatus: SupplierOperationalStatus;
  lastReviewDate?: string;
  nextReviewDate?: string;
}

export const supplierDirectory: Record<number, SupplierDirectoryEntry> = {
  1: {
    supplierId: 1,
    supplierName: 'Millbrook Bakery Supplies',
    supplierCategory: 'Bakery & Ambient',
    supplierType: 'Strategic (Full governance)',
    coverageType: 'Client account',
    operationalStatus: SupplierOperationalStatus.Active,
    lastReviewDate: '2026-02-01',
    nextReviewDate: '2026-08-01',
  },
  2: {
    supplierId: 2,
    supplierName: 'Riverside Fresh Produce',
    supplierCategory: 'Fresh Produce',
    supplierType: 'Strategic (Full governance)',
    coverageType: 'Site group',
    operationalStatus: SupplierOperationalStatus.OnHold,
    lastReviewDate: '2025-12-10',
    nextReviewDate: '2026-06-10',
  },
};

export function getSupplierDirectoryEntry(supplierId: number): SupplierDirectoryEntry {
  return supplierDirectory[supplierId] ?? {
    supplierId,
    supplierName: `Supplier ${supplierId}`,
    supplierCategory: 'Uncategorised',
    supplierType: 'Standard',
    coverageType: 'Individual site',
    operationalStatus: SupplierOperationalStatus.Active,
  };
}

/** Merges a patch into the supplier's directory entry, creating it from the fallback default
 * on first edit (Demo Mode edit screen only - see `SupplierEditDemo/index.tsx`). */
export function updateSupplierDirectoryEntry(supplierId: number, patch: Partial<SupplierDirectoryEntry>): SupplierDirectoryEntry {
  const updated = { ...getSupplierDirectoryEntry(supplierId), ...patch };
  supplierDirectory[supplierId] = updated;
  return updated;
}

/* ---- Legal & Commercial ---- */

export interface SupplierLegalCommercialInfo {
  legalEntityName: string;
  tradingName: string;
  companyNumber: string;
  vatNumber: string;
  paymentTerms: string;
  currency: string;
  minimumOrderValue: number;
  deliveryTerms: string;
  contractStartDate: string;
  contractEndDate: string;
  noticePeriodDays: number;
  renewalDate: string;
  commercialOwner: string;
  procurementOwner: string;
}

const legalCommercialInfo: Record<number, SupplierLegalCommercialInfo> = {
  1: {
    legalEntityName: 'Millbrook Bakery Supplies Ltd',
    tradingName: 'Millbrook Bakery Supplies',
    companyNumber: '04521987',
    vatNumber: 'GB745213698',
    paymentTerms: 'Net 30',
    currency: 'GBP',
    minimumOrderValue: 150,
    deliveryTerms: 'DAP - delivered to site',
    contractStartDate: '2024-04-01',
    contractEndDate: '2026-09-30',
    noticePeriodDays: 60,
    renewalDate: '2026-07-31',
    commercialOwner: 'Priya Shah',
    procurementOwner: 'Tom Ellery',
  },
  2: {
    legalEntityName: 'Riverside Fresh Produce Ltd',
    tradingName: 'Riverside Fresh',
    companyNumber: '06123458',
    vatNumber: 'GB102938475',
    paymentTerms: 'Net 14',
    currency: 'GBP',
    minimumOrderValue: 75,
    deliveryTerms: 'FOB - collected by supplier route',
    contractStartDate: '2025-01-01',
    contractEndDate: '2026-12-31',
    noticePeriodDays: 30,
    renewalDate: '2026-11-01',
    commercialOwner: 'Priya Shah',
    procurementOwner: 'Jodie Marsh',
  },
};

export function getSupplierLegalCommercialInfo(supplierId: number): SupplierLegalCommercialInfo {
  return legalCommercialInfo[supplierId] ?? {
    legalEntityName: '',
    tradingName: '',
    companyNumber: '',
    vatNumber: '',
    paymentTerms: 'Net 30',
    currency: 'GBP',
    minimumOrderValue: 0,
    deliveryTerms: '',
    contractStartDate: '',
    contractEndDate: '',
    noticePeriodDays: 30,
    renewalDate: '',
    commercialOwner: '',
    procurementOwner: '',
  };
}

/** Merges a patch into the supplier's legal & commercial record, creating it from the fallback
 * default on first edit (Demo Mode edit screen only - see `SupplierEditDemo/index.tsx`). */
export function updateSupplierLegalCommercialInfo(supplierId: number, patch: Partial<SupplierLegalCommercialInfo>): SupplierLegalCommercialInfo {
  const updated = { ...getSupplierLegalCommercialInfo(supplierId), ...patch };
  legalCommercialInfo[supplierId] = updated;
  return updated;
}

/* ---- Contacts ---- */

export type SupplierContactType = 'AccountManager' | 'CustomerService' | 'Ordering' | 'Finance' | 'Escalation' | 'OutOfHours';

export interface SupplierContact {
  id: number;
  type: SupplierContactType;
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes?: string;
}

const contactsBySupplier: Record<number, SupplierContact[]> = {
  1: [
    { id: 1, type: 'AccountManager', name: 'Alex Whitfield', role: 'Key Account Manager', email: 'alex.whitfield@millbrookbakery.example', phone: '+44 7700 900101', isPrimary: true },
    { id: 2, type: 'Ordering', name: 'Sam Ordway', role: 'Order Desk', email: 'orders@millbrookbakery.example', phone: '+44 7700 900102', isPrimary: false },
    { id: 3, type: 'Finance', name: 'Nina Cross', role: 'Credit Control', email: 'finance@millbrookbakery.example', phone: '+44 7700 900103', isPrimary: false },
  ],
  2: [
    { id: 4, type: 'AccountManager', name: 'Grace Holloway', role: 'Account Manager', email: 'grace.holloway@riversidefresh.example', phone: '+44 7700 900201', isPrimary: true },
    { id: 5, type: 'Escalation', name: 'Marcus Reid', role: 'Operations Director', email: 'marcus.reid@riversidefresh.example', phone: '+44 7700 900202', isPrimary: false, notes: 'Escalate delivery/quality issues here.' },
  ],
};

export function getSupplierContacts(supplierId: number): SupplierContact[] {
  return contactsBySupplier[supplierId] ?? [];
}

/** Replaces the supplier's contact list (Demo Mode edit screen only - see
 * `SupplierEditDemo/index.tsx`). */
export function setSupplierContacts(supplierId: number, contacts: SupplierContact[]): void {
  contactsBySupplier[supplierId] = contacts;
}

/* ---- Coverage & Sites ---- */

export type SupplierCoverageAssignmentLevel = 'AllSites' | 'ClientAccount' | 'SiteGroup' | 'IndividualSite';

export interface SupplierCoverageAssignment {
  id: number;
  level: SupplierCoverageAssignmentLevel;
  region?: string;
  clientAccount?: string;
  siteGroup?: string;
  site?: string;
  deliveryDays: string[];
  orderCutOff: string;
  leadTimeDays: number;
  minimumOrderValue: number;
  status: 'Active' | 'Inactive';
}

const coverageBySupplier: Record<number, SupplierCoverageAssignment[]> = {
  1: [
    {
      id: 1, level: 'ClientAccount', region: 'South East', clientAccount: 'Greenway Care Group', deliveryDays: ['Monday', 'Wednesday', 'Friday'], orderCutOff: '14:00 D-1', leadTimeDays: 2, minimumOrderValue: 150, status: 'Active',
    },
    {
      id: 2, level: 'IndividualSite', region: 'South East', clientAccount: 'Greenway Care Group', site: 'Site 0145 - Riverside House', deliveryDays: ['Tuesday', 'Thursday'], orderCutOff: '14:00 D-1', leadTimeDays: 2, minimumOrderValue: 100, status: 'Active',
    },
  ],
  2: [
    {
      id: 3, level: 'SiteGroup', region: 'Midlands', siteGroup: 'Midlands Cluster A', deliveryDays: ['Monday', 'Thursday'], orderCutOff: '10:00 D-1', leadTimeDays: 1, minimumOrderValue: 75, status: 'Active',
    },
  ],
};

export function getSupplierCoverageAssignments(supplierId: number): SupplierCoverageAssignment[] {
  return coverageBySupplier[supplierId] ?? [];
}

/* ---- Catalogues (summary, for the Supplier Profile Catalogues tab) ---- */

export interface SupplierCatalogueSummary {
  id: number;
  catalogueName: string;
  catalogueType: string;
  status: 'Active' | 'Draft' | 'Expired' | 'PendingApproval';
  validFrom: string;
  validTo?: string;
  itemCount: number;
  assignedSites: number;
}

const cataloguesBySupplier: Record<number, SupplierCatalogueSummary[]> = {
  1: [
    { id: 1, catalogueName: 'Millbrook Core Range', catalogueType: 'Standard', status: 'Active', validFrom: '2026-01-01', validTo: '2026-12-31', itemCount: 84, assignedSites: 32 },
    { id: 2, catalogueName: 'Millbrook Seasonal Bakery', catalogueType: 'Seasonal', status: 'PendingApproval', validFrom: '2026-09-01', validTo: '2026-12-24', itemCount: 12, assignedSites: 32 },
  ],
  2: [
    { id: 3, catalogueName: 'Riverside Fresh Produce Range', catalogueType: 'Standard', status: 'Active', validFrom: '2026-01-01', itemCount: 46, assignedSites: 18 },
  ],
};

export function getSupplierCatalogues(supplierId: number): SupplierCatalogueSummary[] {
  return cataloguesBySupplier[supplierId] ?? [];
}

/* ---- Documents & Certifications ---- */

export type SupplierDocumentType =
  | 'PublicLiabilityInsurance'
  | 'FoodSafetyCertification'
  | 'BrcSalsaSts'
  | 'HaccpDocuments'
  | 'ModernSlaveryStatement'
  | 'SustainabilityPolicy'
  | 'AllergenPolicy'
  | 'BankDetails'
  | 'Contract';

export type SupplierDocumentStatus = 'Required' | 'Requested' | 'Uploaded' | 'UnderReview' | 'Approved' | 'Expired' | 'Rejected' | 'Waived';

export interface SupplierDocumentItem {
  id: number;
  type: SupplierDocumentType;
  status: SupplierDocumentStatus;
  issueDate?: string;
  expiryDate?: string;
  owner?: string;
  uploadedBy?: string;
  lastReviewed?: string;
}

const documentsBySupplier: Record<number, SupplierDocumentItem[]> = {
  1: [
    { id: 1, type: 'PublicLiabilityInsurance', status: 'Approved', issueDate: '2026-01-01', expiryDate: '2026-12-31', owner: 'Compliance', uploadedBy: 'Alex Whitfield', lastReviewed: '2026-02-01' },
    { id: 2, type: 'FoodSafetyCertification', status: 'Approved', issueDate: '2025-03-01', expiryDate: '2027-03-01', owner: 'Compliance', uploadedBy: 'Alex Whitfield', lastReviewed: '2026-02-01' },
    { id: 3, type: 'BrcSalsaSts', status: 'UnderReview', issueDate: '2024-06-01', expiryDate: '2026-08-15', owner: 'Compliance', uploadedBy: 'Alex Whitfield', lastReviewed: '2026-06-01' },
    { id: 4, type: 'ModernSlaveryStatement', status: 'Approved', owner: 'Compliance', uploadedBy: 'Alex Whitfield', lastReviewed: '2026-02-01' },
    { id: 5, type: 'Contract', status: 'Approved', owner: 'Procurement', uploadedBy: 'Tom Ellery', lastReviewed: '2026-02-01' },
    { id: 6, type: 'BankDetails', status: 'Approved', owner: 'Finance', uploadedBy: 'Nina Cross', lastReviewed: '2026-02-01' },
  ],
  2: [
    { id: 7, type: 'PublicLiabilityInsurance', status: 'Approved', issueDate: '2025-08-01', expiryDate: '2026-08-01', owner: 'Compliance', uploadedBy: 'Grace Holloway', lastReviewed: '2025-12-10' },
    { id: 8, type: 'FoodSafetyCertification', status: 'Expired', issueDate: '2024-01-10', expiryDate: '2026-06-30', owner: 'Compliance', uploadedBy: 'Grace Holloway', lastReviewed: '2025-12-10' },
    { id: 9, type: 'HaccpDocuments', status: 'Requested', owner: 'Compliance' },
    { id: 10, type: 'AllergenPolicy', status: 'Required', owner: 'Compliance' },
    { id: 11, type: 'Contract', status: 'Approved', owner: 'Procurement', uploadedBy: 'Jodie Marsh', lastReviewed: '2025-12-10' },
  ],
};

export function getSupplierDocuments(supplierId: number): SupplierDocumentItem[] {
  return documentsBySupplier[supplierId] ?? [];
}

/** Rolls a supplier's document set up into the single "Certification status" header chip. */
export function getSupplierCertificationStatus(supplierId: number): 'Compliant' | 'ActionNeeded' | 'Expired' {
  const documents = getSupplierDocuments(supplierId);
  if (documents.some((document) => document.status === 'Expired' || document.status === 'Rejected')) return 'Expired';
  if (documents.some((document) => document.status === 'Required' || document.status === 'Requested' || document.status === 'UnderReview')) return 'ActionNeeded';
  return 'Compliant';
}

/* ---- Exceptions ---- */

export type SupplierExceptionCategory =
  | 'MissingDocuments'
  | 'ExpiredCertifications'
  | 'PendingRenewals'
  | 'PoorDeliveryPerformance'
  | 'PricingIssues'
  | 'CatalogueIssues'
  | 'MissingSiteCoverage'
  | 'SupplierRequestsAwaitingReview'
  | 'BlockedSupplierStillAssigned'
  | 'SupplierHasNoActiveCatalogue';

export type SupplierExceptionSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type SupplierExceptionStatus = 'Open' | 'InProgress' | 'Waived' | 'Resolved';

export interface SupplierExceptionItem {
  id: number;
  supplierId: number;
  supplierName: string;
  exception: string;
  category: SupplierExceptionCategory;
  severity: SupplierExceptionSeverity;
  affectedSites: number;
  owner?: string;
  dueDate?: string;
  status: SupplierExceptionStatus;
  lastUpdated: string;
}

/** Flat seed list backing both the standalone Supplier Exceptions screen and the
 * per-supplier Exceptions tab (filtered by supplierId). Local component state only - no
 * backend persistence yet. */
export const supplierExceptionsSeed: SupplierExceptionItem[] = [
  {
    id: 1, supplierId: 2, supplierName: 'Riverside Fresh Produce', exception: 'Food safety certification expired 2026-06-30', category: 'ExpiredCertifications', severity: 'Critical', affectedSites: 18, owner: 'Compliance', dueDate: '2026-07-15', status: 'Open', lastUpdated: '2026-07-01',
  },
  {
    id: 2, supplierId: 2, supplierName: 'Riverside Fresh Produce', exception: 'HACCP documents requested, not yet received', category: 'MissingDocuments', severity: 'High', affectedSites: 18, owner: 'Compliance', dueDate: '2026-07-20', status: 'InProgress', lastUpdated: '2026-06-28',
  },
  {
    id: 3, supplierId: 1, supplierName: 'Millbrook Bakery Supplies', exception: 'BRC/SALSA/STS certification renewal due within 60 days', category: 'PendingRenewals', severity: 'Medium', affectedSites: 32, owner: 'Procurement', dueDate: '2026-08-15', status: 'Open', lastUpdated: '2026-06-15',
  },
  {
    id: 4, supplierId: 1, supplierName: 'Millbrook Bakery Supplies', exception: 'Seasonal bakery catalogue pending commercial price approval', category: 'PricingIssues', severity: 'Medium', affectedSites: 32, owner: 'Commercial', dueDate: '2026-08-01', status: 'Open', lastUpdated: '2026-07-02',
  },
  {
    id: 5, supplierId: 2, supplierName: 'Riverside Fresh Produce', exception: 'On-time delivery has dropped below 85% over the last 30 days', category: 'PoorDeliveryPerformance', severity: 'High', affectedSites: 18, owner: 'Procurement', status: 'Open', lastUpdated: '2026-07-03',
  },
  {
    id: 6, supplierId: 3, supplierName: 'Northgate Dry Goods', exception: 'Supplier onboarding request awaiting procurement review', category: 'SupplierRequestsAwaitingReview', severity: 'Low', affectedSites: 0, status: 'Open', lastUpdated: '2026-07-05',
  },
  {
    id: 7, supplierId: 4, supplierName: 'Coastal Seafood Co', exception: 'Supplier is on hold but still assigned to 6 active sites', category: 'BlockedSupplierStillAssigned', severity: 'Critical', affectedSites: 6, owner: 'Procurement', status: 'Open', lastUpdated: '2026-06-30',
  },
  {
    id: 8, supplierId: 4, supplierName: 'Coastal Seafood Co', exception: 'No active catalogue on file for this supplier', category: 'SupplierHasNoActiveCatalogue', severity: 'High', affectedSites: 6, owner: 'Procurement', status: 'Open', lastUpdated: '2026-06-30',
  },
];

export function getSupplierExceptions(supplierId: number): SupplierExceptionItem[] {
  return supplierExceptionsSeed.filter((exception) => exception.supplierId === supplierId);
}

/* ---- Activity ---- */

export interface SupplierActivityEntry {
  id: number;
  date: string;
  actor: string;
  action: string;
}

const activityBySupplier: Record<number, SupplierActivityEntry[]> = {
  1: [
    { id: 1, date: '2026-07-02', actor: 'Priya Shah (Commercial)', action: 'Submitted seasonal bakery price change for approval.' },
    { id: 2, date: '2026-06-15', actor: 'Tom Ellery (Procurement)', action: 'Flagged BRC/SALSA/STS certification renewal due within 60 days.' },
    { id: 3, date: '2026-02-01', actor: 'System', action: 'Annual review completed - approval status confirmed.' },
  ],
  2: [
    { id: 4, date: '2026-07-03', actor: 'System', action: 'On-time delivery performance dropped below 85% threshold.' },
    { id: 5, date: '2026-06-28', actor: 'Grace Holloway (Supplier)', action: 'Requested extension for HACCP document submission.' },
    { id: 6, date: '2025-12-10', actor: 'Jodie Marsh (Procurement)', action: 'Completed onboarding review and approved supplier.' },
  ],
};

export function getSupplierActivity(supplierId: number): SupplierActivityEntry[] {
  return activityBySupplier[supplierId] ?? [];
}
